import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast, type IChatMessage } from '@kognitos/lattice';
import { useListThreadMessagesInfinite } from '@/generated/hooks/zen/v1/thread_service';

import { captureException } from '@/modules/sentry';
import {
  selectQuillChatIsProcessed,
  selectQuillChatMarkProcessed,
  selectQuillChatUpdateMessages,
  useQuillChatStore,
} from '@/shared/stores';
import { errorHandler } from '@/shared/utils/error-handler';

import { QUILL_AGENT_NAME } from '../constants';
import type {
  IQuillChatConnectionRequestMessage,
  IQuillChatSelectedConnection,
} from '../types';
import { processBatchMessages } from '../utils';
import type { V1ThreadMessage } from '@/generated/hooks/zen/models';

const MESSAGES_PER_PAGE = 50;

/**
 * Ref to track the scroll container for preserving scroll position.
 */
interface IScrollContainerRef {
  current: HTMLElement | null;
}

/**
 * Parameters for the useQuillThreadPreviousMessages hook.
 */
interface IUseQuillThreadPreviousMessagesParams {
  /** Automation ID for store keying */
  automationId: string;
  /** Thread ID to fetch messages for */
  threadId: string | undefined;
  /** Callback for connection selection */
  onConnectionSelect: (
    connection: IQuillChatSelectedConnection,
    connectionRequest: IQuillChatConnectionRequestMessage,
    messageId: string,
  ) => Promise<void>;
  /** Callback for file clicks */
  onFileClick: (file: string) => void;
  /** Existing messages for duplicate checking */
  existingMessages: IChatMessage[];
  /** Callback to set processing state to true */
  setIsProcessingMessageTrue: () => void;
  /** Callback to set processing state to false */
  setIsProcessingMessageFalse: () => void;
  /** Callback to invalidate automation query */
  invalidateAutomationsQuery: () => void;
  /** Current organization ID */
  currentOrganizationId: string;
  /** Current workspace ID */
  currentWorkspaceId: string;
  /** Ref to scroll container for preserving scroll position */
  scrollContainerRef?: IScrollContainerRef;
  /** Callback when previous messages are loaded (first page). Receives the latest message for stream resume decisions. */
  onPreviousMessagesLoaded?: (
    latestMessage: V1ThreadMessage | undefined,
  ) => void;
  /** Callback when an execution_reference artifact is found in previous messages */
  onExecutionIdFound?: (executionId: string) => void;
}

/**
 * Hook to fetch and process previous thread messages on-demand.
 * Processes messages incrementally as pages are fetched and prepends them to existing messages.
 * Uses Zustand store directly for message state management.
 *
 * @param {IUseQuillThreadPreviousMessagesParams} params - Hook parameters
 * @returns {Object} Hook return value
 * @returns {boolean} returns.isLoading - Initial loading state (first page)
 * @returns {boolean} returns.isFetchingMore - Loading state when fetching additional pages
 * @returns {() => Promise<void>} returns.fetchNextPage - Function to fetch next page of messages
 * @returns {boolean} returns.hasNextPage - Whether there are more pages to fetch
 */
export function useQuillThreadPreviousMessages({
  automationId,
  threadId,
  onConnectionSelect,
  onFileClick,
  existingMessages,
  setIsProcessingMessageTrue,
  setIsProcessingMessageFalse,
  invalidateAutomationsQuery,
  currentOrganizationId,
  currentWorkspaceId,
  scrollContainerRef,
  onPreviousMessagesLoaded,
  onExecutionIdFound,
}: IUseQuillThreadPreviousMessagesParams) {
  const { t } = useTranslation('automation');

  // Store actions (stable references)
  const updateMessages = useQuillChatStore(selectQuillChatUpdateMessages);
  const isProcessed = useQuillChatStore(selectQuillChatIsProcessed);
  const markProcessed = useQuillChatStore(selectQuillChatMarkProcessed);

  // Track processed pages locally
  const processedPagesRef = useRef<Set<number>>(new Set());
  const processedThreadIdRef = useRef<string | undefined>(undefined);

  // Track add_connection messages that need to update earlier connection_request messages
  const addConnectionQueueRef = useRef<
    Map<
      string,
      {
        connectionId: string;
        bookName: string;
        bookVersion: string;
      }
    >
  >(new Map());

  // Store latest callback values in refs to avoid unnecessary re-runs
  const callbacksRef = useRef({
    onConnectionSelect,
    onFileClick,
    setIsProcessingMessageTrue,
    setIsProcessingMessageFalse,
    invalidateAutomationsQuery,
    onPreviousMessagesLoaded,
    onExecutionIdFound,
  });
  const existingMessagesRef = useRef(existingMessages);

  // Update refs when values change
  callbacksRef.current = {
    onConnectionSelect,
    onFileClick,
    setIsProcessingMessageTrue,
    setIsProcessingMessageFalse,
    invalidateAutomationsQuery,
    onPreviousMessagesLoaded,
    onExecutionIdFound,
  };
  existingMessagesRef.current = existingMessages;

  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isStale: isStalePreviousMessages,
  } = useListThreadMessagesInfinite(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    QUILL_AGENT_NAME,
    threadId || '',
    { page_size: MESSAGES_PER_PAGE },
    {
      query: {
        enabled: Boolean(threadId),
        initialPageParam: undefined,
        getNextPageParam: lastPage =>
          lastPage.data.next_page_token &&
          lastPage.data.next_page_token.trim() !== ''
            ? lastPage.data.next_page_token
            : undefined,
        refetchOnMount: 'always',
        meta: {
          onError: (error: Error) => {
            errorHandler(error, () => {
              toast.error(t('automation.quillChat.error.loadPreviousMessages'));
              captureException(error, {
                tags: {
                  component: 'useQuillThreadPreviousMessages',
                  action: 'loadPreviousMessages',
                },
              });
            });
          },
        },
      },
    },
  );

  // Process messages incrementally as pages are fetched
  // eslint-disable-next-line no-restricted-syntax -- reason: Process each page as it arrives and prepend to existing messages
  useEffect(() => {
    if (processedThreadIdRef.current !== threadId) {
      processedThreadIdRef.current = threadId;
      processedPagesRef.current.clear();
      addConnectionQueueRef.current.clear();
    }

    if (
      !infiniteData ||
      isLoading ||
      isFetchingNextPage ||
      !automationId ||
      isStalePreviousMessages
    ) {
      return;
    }

    // Process each page that hasn't been processed yet
    infiniteData.pages.forEach((page, pageIndex) => {
      if (processedPagesRef.current.has(pageIndex)) {
        return;
      }

      const messages = page.data.thread_messages ?? [];
      if (messages.length === 0) {
        processedPagesRef.current.add(pageIndex);
        return;
      }

      // Create a wrapper that uses the store for checking/marking processed IDs
      const processedMessageIds = {
        has: (id: string) => isProcessed(automationId, id),
        add: (id: string) => markProcessed(automationId, id),
      } as Set<string>;

      // Process messages in their original order (newest first from API)
      const result = processBatchMessages(messages, {
        onConnectionSelect: callbacksRef.current.onConnectionSelect,
        onFileClick: callbacksRef.current.onFileClick,
        processedMessageIds,
        existingMessages: existingMessagesRef.current,
        addConnectionQueue: addConnectionQueueRef.current,
        threadId,
        automationId,
      });

      // Preserve scroll position before prepending messages
      const scrollContainer = scrollContainerRef?.current;
      const previousScrollHeight = scrollContainer?.scrollHeight ?? 0;
      const previousScrollTop = scrollContainer?.scrollTop ?? 0;

      // Prepend new messages to existing messages in store
      updateMessages(automationId, prev => {
        // Filter out any messages that are already in the new batch to avoid duplicates
        const existingIds = new Set(prev.map(msg => msg.id));
        const newMessages = result.messages.filter(
          msg => !existingIds.has(msg.id),
        );
        return [...newMessages, ...prev];
      });

      // Restore scroll position after DOM update
      if (scrollContainer) {
        requestAnimationFrame(() => {
          if (scrollContainer) {
            const newScrollHeight = scrollContainer.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            scrollContainer.scrollTop = previousScrollTop + scrollDifference;
          }
        });
      }

      // Only handle status updates for the first page (latest messages)
      // Older messages fetched via pagination are historical and shouldn't trigger status updates
      if (pageIndex === 0) {
        // Only seed processing state from this HTTP snapshot before the live
        // stream has produced anything (store empty). Otherwise a late response
        // snapshotted before the agent finished would re-set processing=true
        // and leave the synthetic "Did you know?" builder-progress widget
        // stuck. Mirrors the onPreviousMessagesLoaded gate below.
        if (existingMessagesRef.current.length === 0) {
          if (result.isProcessing) {
            callbacksRef.current.setIsProcessingMessageTrue();
          } else {
            callbacksRef.current.setIsProcessingMessageFalse();
          }
        }

        // Invalidate automation query if any automation was built
        if (result.shouldInvalidateAutomation) {
          callbacksRef.current.invalidateAutomationsQuery();
        }

        // Scan for execution_reference artifacts to restore browser session on page refresh
        // Look for the most recent execution ID - useBrowserSessionForExecution will handle
        // the case where the browser was released (it simply won't find browser session data)
        for (const message of messages) {
          if (
            message.artifact?.artifact_type === 'execution_reference' &&
            message.artifact.content
          ) {
            try {
              const executionRef = JSON.parse(message.artifact.content);
              if (executionRef.execution_id) {
                callbacksRef.current.onExecutionIdFound?.(
                  executionRef.execution_id,
                );
                break; // Only need the most recent execution ID
              }
            } catch {
              // Ignore parse errors
            }
          }
        }

        // Notify parent with the latest message for stream resume decisions
        // The first message in the array is the latest (API returns newest first)
        const latestMessage = messages[0];
        // Only call this when there are no messages in the store
        if (existingMessagesRef.current.length === 0) {
          callbacksRef.current.onPreviousMessagesLoaded?.(latestMessage);
        }
      }

      // Mark this page as processed
      processedPagesRef.current.add(pageIndex);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollContainerRef is a ref, store actions are stable
  }, [infiniteData, isLoading, isFetchingNextPage, automationId, threadId]);

  return {
    // Only claim to be loading when we genuinely have no response yet for
    // this threadId. `isLoading` from useInfiniteQuery is true on the
    // first fetch; gating it on `!infiniteData` is a hard guard so that
    // a settled empty-thread response (data = { pages: [{ ..., messages: [] }] })
    // immediately drops the loading flag and lets the lattice Chat
    // render its empty-state placeholder instead of a stuck skeleton.
    // (Surfaced by quill-thread-create-rename-switch nightly story:
    // freshly-created and switched-to empty threads were rendering
    // chat-loading-skeleton instead of "No messages yet".)
    isLoading: isLoading && !infiniteData && !isFetchingNextPage,
    isFetchingMore: isFetchingNextPage, // Fetching additional pages
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    previousMessagesData: infiniteData,
  };
}
